class DiyShowcase {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-diy-showcase-track]');
    this.prev = root.querySelector('[data-diy-showcase-prev]');
    this.next = root.querySelector('[data-diy-showcase-next]');
    this.originalSlides = Array.from(root.querySelectorAll('[data-diy-showcase-slide]'));
    this.slides = this.originalSlides.slice();

    this.enableLoop = this.originalSlides.length > 1;
    this.setSize = this.originalSlides.length;
    this.middleStart = 0;
    this.middleEnd = Math.max(0, this.originalSlides.length - 1);
    this.firstRealIndex = 0;
    this.lastRealIndex = Math.max(0, this.originalSlides.length - 1);

    this.currentIndex = 0;
    this.minIndex = 0;
    this.maxIndex = Math.max(0, this.slides.length - 1);

    this.scrollEndTimer = null;
    this.fallbackTimer = null;
    this.isSnapping = false;
    this.settlingFromSnap = false;
    this.isJumping = false;
    this.isReady = false;
    this.pendingDelta = 0;

    if (!this.track || this.originalSlides.length === 0) return;

    this.root.classList.remove('diy-showcase--ready');
    if (this.prev) this.prev.disabled = true;
    if (this.next) this.next.disabled = true;
    if (this.enableLoop) this.buildLoopTrack();
    this.currentIndex = this.firstRealIndex;

    this.bindEvents();
    this.initializePosition();
  }

  buildLoopTrack() {
    const firstOriginal = this.originalSlides[0];
    if (!firstOriginal) return;

    const beforeFrag = document.createDocumentFragment();
    const afterFrag = document.createDocumentFragment();

    this.originalSlides.forEach((slide) => {
      const beforeClone = slide.cloneNode(true);
      beforeClone.dataset.loopCopy = 'before';
      beforeFrag.appendChild(beforeClone);

      const afterClone = slide.cloneNode(true);
      afterClone.dataset.loopCopy = 'after';
      afterFrag.appendChild(afterClone);
    });

    this.track.insertBefore(beforeFrag, firstOriginal);
    this.track.appendChild(afterFrag);

    this.slides = Array.from(this.track.querySelectorAll('[data-diy-showcase-slide]'));

    this.setSize = this.originalSlides.length;
    this.middleStart = this.setSize;
    this.middleEnd = this.setSize * 2 - 1;
    this.firstRealIndex = this.middleStart;
    this.lastRealIndex = this.middleEnd;
    this.maxIndex = this.slides.length - 1;
  }

  bindEvents() {
    if (this.prev) {
      this.prev.addEventListener('click', (event) => {
        event.preventDefault();
        this.go(-1);
      });
    }

    if (this.next) {
      this.next.addEventListener('click', (event) => {
        event.preventDefault();
        this.go(1);
      });
    }

    this.track.addEventListener('scroll', () => {
      if (this.isJumping) return;
      if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = setTimeout(() => {
        this.handleScrollSettled();
      }, 120);
    });

    window.addEventListener('resize', () => {
      if (this.enableLoop) {
        const logical = this.getLogicalIndex(this.getNearestIndex());
        this.currentIndex = this.getRealIndexFromLogical(logical);
      } else {
        this.recalculateBounds();
        this.currentIndex = this.clampIndex(this.getNearestIndex());
      }
      this.scrollToIndex(this.currentIndex, 'auto');
      this.updateUi();
    });
  }

  initializePosition() {
    const doAlign = () => {
      this.isJumping = true;
      if (!this.enableLoop) this.recalculateBounds();
      this.currentIndex = this.firstRealIndex;
      this.scrollToIndex(this.currentIndex, 'auto');
      requestAnimationFrame(() => {
        this.scrollToIndex(this.currentIndex, 'auto');
        requestAnimationFrame(() => {
          this.scrollToIndex(this.currentIndex, 'auto');
          this.isJumping = false;
          this.isReady = true;
          this.root.classList.add('diy-showcase--ready');
          this.updateUi();
        });
      });
    };

    const images = Array.from(this.track.querySelectorAll('img'));
    const pending = images.filter((img) => !img.complete);
    if (!pending.length) {
      requestAnimationFrame(doAlign);
      return;
    }

    let finished = false;
    const finishOnce = () => {
      if (finished) return;
      finished = true;
      doAlign();
    };

    let remaining = pending.length;
    const oneDone = () => {
      remaining -= 1;
      if (remaining <= 0) finishOnce();
    };

    pending.forEach((img) => {
      img.addEventListener('load', oneDone, { once: true });
      img.addEventListener('error', oneDone, { once: true });
    });

    window.setTimeout(finishOnce, 500);
  }

  go(direction) {
    if (!this.isReady || this.isJumping) return;
    if (this.isSnapping) {
      this.pendingDelta = Math.max(-12, Math.min(12, this.pendingDelta + direction));
      return;
    }

    if (!this.enableLoop) {
      this.recalculateBounds();
      const baseIndex = this.clampIndex(this.getNearestIndex());
      const nextIndex = this.clampIndex(baseIndex + direction);
      if (nextIndex === baseIndex) {
        this.currentIndex = baseIndex;
        this.updateUi();
        return;
      }
      this.startSnap(nextIndex);
      return;
    }

    const nearest = this.getNearestIndex();
    const logical = this.getLogicalIndex(nearest);
    const baseIndex = this.getRealIndexFromLogical(logical);
    const target = Math.max(0, Math.min(baseIndex + direction, this.maxIndex));

    this.scrollToIndex(baseIndex, 'auto');
    this.currentIndex = baseIndex;
    this.startSnap(target);
  }

  handleScrollSettled() {
    if (!this.isReady || this.isJumping) return;

    const nearest = this.getNearestIndex();

    if (this.settlingFromSnap) {
      this.finishSnap(nearest);
      return;
    }

    if (!this.enableLoop) {
      this.recalculateBounds();
      const clamped = this.clampIndex(nearest);
      if (clamped !== nearest) {
        this.startSnap(clamped);
      } else {
        this.currentIndex = clamped;
        this.updateUi();
      }
      return;
    }

    this.currentIndex = nearest;
    this.startSnap(nearest);
  }

  startSnap(index) {
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
    this.currentIndex = index;
    this.isSnapping = true;
    this.settlingFromSnap = true;
    const startLeft = this.track.scrollLeft;
    const targetLeft = this.scrollToIndex(index, 'smooth');

    if (Math.abs(targetLeft - startLeft) < 1) {
      this.finishSnap(index);
      return;
    }

    this.fallbackTimer = window.setTimeout(() => {
      if (!this.settlingFromSnap) return;
      this.finishSnap();
    }, 420);
  }

  finishSnap(forcedIndex = null) {
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
    this.isSnapping = false;
    this.settlingFromSnap = false;
    this.currentIndex = forcedIndex == null ? this.getNearestIndex() : forcedIndex;
    if (this.enableLoop) this.recenterIfNeeded();
    this.updateUi();
    this.flushPendingDelta();
  }

  flushPendingDelta() {
    if (!this.isReady || this.isSnapping || this.isJumping) return;
    if (this.pendingDelta === 0) return;

    const delta = this.pendingDelta;
    this.pendingDelta = 0;

    if (!this.enableLoop) {
      this.recalculateBounds();
      const base = this.clampIndex(this.getNearestIndex());
      const target = this.clampIndex(base + delta);
      if (target !== base) this.startSnap(target);
      return;
    }

    const nearest = this.getNearestIndex();
    const logical = this.getLogicalIndex(nearest);
    const base = this.getRealIndexFromLogical(logical);
    const target = Math.max(0, Math.min(base + delta, this.maxIndex));
    if (target === base) return;

    this.scrollToIndex(base, 'auto');
    this.currentIndex = base;
    this.startSnap(target);
  }

  recenterIfNeeded() {
    if (!this.enableLoop || this.setSize < 2) return;

    if (this.currentIndex < this.middleStart || this.currentIndex > this.middleEnd) {
      const logical = this.getLogicalIndex(this.currentIndex);
      const recentered = this.getRealIndexFromLogical(logical);
      this.jumpToIndex(recentered);
    }
  }

  jumpToIndex(index) {
    this.isJumping = true;
    this.currentIndex = index;
    this.scrollToIndex(index, 'auto');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.isJumping = false;
        this.updateUi();
      });
    });
  }

  getNearestIndex() {
    return this.getNearestIndexForCenter(this.track.scrollLeft + this.track.clientWidth / 2);
  }

  getNearestIndexForCenter(trackCenter) {
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(slideCenter - trackCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  getLogicalIndex(index) {
    if (!this.enableLoop) return index;
    const normalized = ((index - this.middleStart) % this.setSize + this.setSize) % this.setSize;
    return normalized;
  }

  getRealIndexFromLogical(logicalIndex) {
    if (!this.enableLoop) return logicalIndex;
    const normalized = ((logicalIndex % this.setSize) + this.setSize) % this.setSize;
    return this.middleStart + normalized;
  }

  recalculateBounds() {
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    this.minIndex = this.getNearestIndexForCenter(this.track.clientWidth / 2);
    this.maxIndex = this.getNearestIndexForCenter(maxScroll + this.track.clientWidth / 2);
    if (this.maxIndex < this.minIndex) this.maxIndex = this.minIndex;
  }

  clampIndex(index) {
    return Math.max(this.minIndex, Math.min(index, this.maxIndex));
  }

  scrollToIndex(index, behavior) {
    const slide = this.slides[index];
    if (!slide) return this.track.scrollLeft;
    const left = slide.offsetLeft - (this.track.clientWidth - slide.clientWidth) / 2;
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const targetLeft = Math.min(maxScroll, Math.max(0, left));
    if (behavior === 'auto') {
      this.track.scrollLeft = targetLeft;
    } else {
      this.track.scrollTo({ left: targetLeft, behavior });
    }
    return targetLeft;
  }

  updateUi() {
    if (!this.prev || !this.next) return;

    if (!this.isReady) {
      this.prev.disabled = true;
      this.next.disabled = true;
      return;
    }

    if (this.enableLoop) {
      this.prev.disabled = false;
      this.next.disabled = false;
      return;
    }

    this.recalculateBounds();
    this.currentIndex = this.clampIndex(this.getNearestIndex());
    this.prev.disabled = this.currentIndex <= this.minIndex;
    this.next.disabled = this.currentIndex >= this.maxIndex;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-diy-showcase]').forEach((root) => {
    new DiyShowcase(root);
  });
});
